import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/design-system/ui/Button';
import { Counter } from '@/design-system/ui/Controls';
// import { Segmented } from '@/design-system/ui/Controls';
import { Combobox } from '@/design-system/ui/Combobox';
import { Icon } from '@/design-system/ui/Icon';
import { JalaliDatePicker } from '@/design-system/ui/JalaliDatePicker';
import {
  addDays,
  apiDateToJalali,
  formatJalaliWithWeekday,
  todayJalali,
  type JalaliDate,
} from '@/core/format/jalali';
import { storage } from '@/core/storage/storage';
import { useCities } from '@/features/catalog/api/catalog-api';
import {
  defaultSearchDate,
  jalaliToSearchDate,
  MAX_PASSENGERS,
  toSearchParams,
  type TripSearch,
  type TripType,
} from '../model/search-params';

const LAST_KEY = 'last-search';

interface Props {
  initial?: Partial<TripSearch>;
}

export function TripSearchForm({ initial }: Props) {
  const navigate = useNavigate();
  const cities = useCities();
  const seed = { ...storage.get<Partial<TripSearch>>(LAST_KEY, {}), ...initial };

  const [origin, setOrigin] = useState(seed.origin ?? '');
  const [destination, setDestination] = useState(seed.destination ?? '');
  const [date, setDate] = useState<JalaliDate>(
    (initial?.date ? apiDateToJalali(initial.date) : null) ?? defaultSearchDate(),
  );
  const [passengers, setPassengers] = useState(seed.passengers ?? 1);
  // سفر دربستی غیرفعال شده است؛ همیشه سفر عادی
  const [type] = useState<TripType>('normal');
  // const [type, setType] = useState<TripType>(seed.type ?? 'normal');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [errors, setErrors] = useState<{ origin?: string; destination?: string }>({});

  const [prevInitial, setPrevInitial] = useState(initial);
  if (prevInitial !== initial) {
    setPrevInitial(initial);
    if (initial?.origin) setOrigin(initial.origin);
    if (initial?.destination) setDestination(initial.destination);
    if (initial?.date) setDate(apiDateToJalali(initial.date) ?? defaultSearchDate());
    if (initial?.passengers) setPassengers(initial.passengers);
    // if (initial?.type) setType(initial.type);
    if (initial?.origin || initial?.destination) setErrors({});
  }

  const swap = () => {
    setOrigin(destination);
    setDestination(origin);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!origin) next.origin = 'ایستگاه مبدأ را وارد کنید';
    if (!destination) next.destination = 'ایستگاه مقصد را وارد کنید';
    else if (destination === origin) next.destination = 'مبدأ و مقصد نمی‌توانند یکسان باشند';
    setErrors(next);
    if (Object.keys(next).length) return;

    const search: TripSearch = {
      origin,
      destination,
      date: jalaliToSearchDate(date),
      passengers,
      type,
    };
    storage.set(LAST_KEY, { origin, destination, passengers, type });
    navigate(`/trips?${toSearchParams(search)}`);
  };

  const loading = cities.isPending;
  const originsList = cities.data?.origins ?? [];
  const destinationsList = cities.data?.destinations ?? [];

  return (
    <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
      {/* سفر دربستی غیرفعال شده است
      <Segmented<TripType>
        ariaLabel="نوع سفر"
        value={type}
        onChange={setType}
        className="[&_button]:py-3 [&_button]:text-base [&_svg]:size-5"
        options={[
          { value: 'normal', icon: 'car', label: 'سفر عادی' },
          { value: 'exclusive', icon: 'pin', label: 'سفر دربستی' },
        ]}
      />
      */}

      <div className="relative grid grid-cols-1 gap-0 md:grid-cols-2">
        <Combobox
          label="مبدأ"
          containerClassName="[&_label]:sr-only"
          value={origin}
          onChange={(v) => {
            setOrigin(v);
            setErrors((prev) => {
              const rest = { ...prev };
              delete rest.origin;
              return rest;
            });
          }}
          options={originsList}
          disabled={loading}
          error={errors.origin}
          placeholder={loading ? 'در حال بارگذاری…' : 'مبدأ (شهر)'}
          emptyText="ایستگاهی با این نام یافت نشد"
          className="rounded-b-none border-b-0 py-3.5 ps-5 text-base md:rounded-e-none md:rounded-b-md md:border-b"
        />
        <button
          type="button"
          onClick={swap}
          aria-label="جابجایی مبدأ و مقصد"
          className="group border-line bg-paper text-navy ring-paper hover:border-turquoise/50 hover:bg-primary-light hover:text-action absolute top-1/2 left-3 z-10 grid size-10 -translate-y-1/2 place-items-center rounded-full border shadow-sm ring-4 transition active:scale-95 md:left-1/2 md:-translate-x-1/2"
        >
          <Icon
            name="swap"
            size={18}
            className="rotate-90 transition-transform duration-300 group-active:rotate-[270deg] md:rotate-0 md:group-active:rotate-180"
          />
        </button>
        <Combobox
          label="مقصد"
          containerClassName="[&_label]:sr-only"
          value={destination}
          onChange={(v) => {
            setDestination(v);
            setErrors((prev) => {
              const rest = { ...prev };
              delete rest.destination;
              return rest;
            });
          }}
          options={destinationsList}
          disabled={loading}
          error={errors.destination}
          placeholder={loading ? 'در حال بارگذاری…' : 'مقصد (شهر)'}
          emptyText="ایستگاهی با این نام یافت نشد"
          className="rounded-t-none py-3.5 ps-5 text-base md:rounded-s-none md:rounded-t-md"
        />
      </div>

      {cities.isError && (
        <p role="alert" className="text-danger text-sm">
          دریافت لیست شهرها ناموفق بود.{' '}
          <button type="button" className="font-bold underline" onClick={() => cities.refetch()}>
            تلاش مجدد
          </button>
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <span className="text-ink-2 text-sm font-medium">تاریخ سفر</span>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="border-line bg-fill hover:bg-paper flex items-center justify-between rounded-md border px-5 py-3.5 text-base transition"
          >
            <span>{formatJalaliWithWeekday(date)}</span>
            <Icon name="calendar" size={22} className="text-ink-2" />
          </button>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-ink-2 text-sm font-medium">تعداد مسافر</span>
          <div className="border-line bg-fill flex items-center justify-between rounded-md border px-5 py-2">
            <Icon name="users" size={22} className="text-ink-2" />
            <Counter
              label="مسافر"
              value={passengers}
              min={1}
              max={MAX_PASSENGERS}
              onChange={setPassengers}
            />
          </div>
        </div>
      </div>

      <Button
        type="submit"
        size="lg"
        fullWidth
        className="h-[58px] text-[17px]"
        icon={<Icon name="search" size={22} />}
      >
        جستجو
      </Button>

      <JalaliDatePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={date}
        onChange={setDate}
        min={todayJalali()}
        max={addDays(todayJalali(), 60)}
        title="تاریخ سفر"
      />
    </form>
  );
}
